/* eslint-disable max-lines */
import { Controller } from "stimulus";
import SelectedOption from "../../classes/selectedOption";
import debounce from "../../functions/debounce";
import buildURLWithQuery from "../../functions/buildURLWithQuery";
import fetchHtml from "../../functions/fetchHtml";
import keyboardNavigate from "../../functions/dropdowns/keyboardNavigate";
import dispatchCustomEvent from "../../functions/dispatchCustomEvent.js";
import  DOMPurify  from "dompurify";

const optionSelector = "[role='option']:not([aria-disabled])";
const activeSelector = "[aria-selected='true']";

export default class extends Controller {
  static get targets() {
    return ["input", "checkbox", "selectAllCheckbox", "hidden", "results", "selected"];
  }

  static get classes() {
    return ["selected"];
  }

  static get values() {
    return { ready: Boolean, url: String, id: String, name: String, preselected: String };
  }

  connect() {
    this.close();
    this.inputTarget.setAttribute("spellcheck", "false");
    this.mouseDown = false;
    this.onInputChange = debounce(this.onInputChange.bind(this), 300);
    this.keyboardNavigate = keyboardNavigate;
    this.togglePlaceholderHighlighted();
  }

  onInputSelect() {
    this.fetchResults("");
  }

  onEnterKeydown() {
    if (!this.selectedOption) return;
    const selected = this.selectedOption;
    this.inputTarget.value = "";

    const selectAllCheckboxInput = this.selectAllCheckboxInput;
    if (selected.dataset.optionValue === "select-all") {
      selectAllCheckboxInput.checked = !selectAllCheckboxInput.checked;
      this.onToggleSelectAll();
      return;
    }

    const hiddenInputForSelected = this.findHiddenInputForCheckbox(selected);
    const checkbox = selected.querySelector("input");
    if (hiddenInputForSelected) {
      checkbox.checked = false;
      hiddenInputForSelected.remove();
    } else {
      checkbox.checked = true;
      SelectedOption.addTo(
        this, this.nameValue, selected.dataset.formSubmissionValue,
        selected.dataset.placeholderValue
      );
    }
    dispatchCustomEvent(this.inputTarget, "change");
    this.setPlaceholder();
    selectAllCheckboxInput.checked = this.allCheckboxesChecked;
  }

  togglePlaceholderHighlighted() {
    if (this.selectedTargets.length) {
      this.inputTarget.classList.add("dropdown-search-input-modified");
    } else {
      this.inputTarget.classList.remove("dropdown-search-input-modified");
    }
  }

  onInputBlur() {
    if (this.mouseDown) return;
    this.close();
  }

  setPlaceholder() {
    const input = this.inputTarget;

    const hiddenInputForSelectAllCheckbox = this.findHiddenInputForCheckbox(this.selectAllCheckboxTarget);

    let selectedCount = this.selectedTargets.length;
    if (hiddenInputForSelectAllCheckbox) {
      selectedCount = this.selectedTargets.length - 1;
    }

    if (selectedCount && hiddenInputForSelectAllCheckbox) {
      const filteredTargets = this.selectedTargets.filter((target) => target.dataset.optionValue !== "select-all");
      const inputPlaceholderValue = filteredTargets[0].dataset.placeholderValue;
      input.placeholder = `${inputPlaceholderValue}... (1 of ${selectedCount})`;
    } else if (selectedCount) {
      const inputPlaceholderValue = this.selectedTargets[0].dataset.placeholderValue;
      input.placeholder = `${inputPlaceholderValue}... (1 of ${selectedCount})`;
    } else {
      input.placeholder = "Type to search...";
    }
    this.togglePlaceholderHighlighted();
  }

  clear() {
    this.inputTarget.value = "";
  }

  findHiddenInputForCheckbox(selectedField) {
    const selectedFormSubmissionValue = selectedField.dataset.formSubmissionValue;
    return this.selectedTargets.find((t) => t.value === selectedFormSubmissionValue );
  }

  onResultsClick(event) {
    if (!(event.srcElement instanceof HTMLInputElement)) return;

    this.inputTarget.value = "";
    const selected = event.target.closest(optionSelector);

    if (selected.dataset.optionValue === "select-all") {
      this.onToggleSelectAll();
      return;
    }

    const hiddenInputForSelected = this.findHiddenInputForCheckbox(selected);
    if (hiddenInputForSelected) {
      hiddenInputForSelected.remove();
    } else {
      SelectedOption.addTo(
        this, this.nameValue, selected.dataset.formSubmissionValue,
        selected.dataset.placeholderValue
      );
    }
    dispatchCustomEvent(this.inputTarget, "change");
    this.setPlaceholder();
    this.inputTarget.focus();
  }

  onToggleSelectAll() {
    if (this.selectAllCheckboxInput.checked) {
      this._selectAllCheckboxes();
      this.checkboxTargets.forEach((checkbox) => this.addHiddenFieldForOption(checkbox));
    } else {
      this._unSelectAllCheckboxes();
      this.selectedTargets.forEach((input) => input.remove());
    }

    dispatchCustomEvent(this.inputTarget, "change");
    this.setPlaceholder();
    this.inputTarget.focus();
  }

  get allCheckboxesChecked() {
    return this.checkboxTargets.every((target) => {
      const checkbox = target.querySelector("input");
      return checkbox.checked;
    });
  }

  addHiddenFieldForOption(checkbox) {
    if (this.findHiddenInputForCheckbox(checkbox)) return;
    SelectedOption.addTo(
      this, this.nameValue, checkbox.dataset.formSubmissionValue,
      checkbox.dataset.placeholderValue
    );
  }

  _toggleSelectAllCheckbox() {
    const checkbox = this.selectAllCheckboxInput;
    checkbox.checked = !checkbox.checked;
  }

  _selectAllCheckboxes() {
    this.checkboxTargets.forEach((target) => {
      const checkbox = target.querySelector("input");
      checkbox.checked = true;
    });
  }

  _unSelectAllCheckboxes() {
    this.checkboxTargets.forEach((target) => {
      const checkbox = target.querySelector("input");
      checkbox.checked = false;
    });
  }

  checkDropdownValues() {
    const selectAllCheckbox = this.selectAllCheckboxTarget;
    const selectedValues = this.selectedTargets.map((t) => t.value);

    this.resultsTarget.children.forEach((child) => {
      if (selectedValues.includes(child.dataset.formSubmissionValue)) {
        child.querySelector("input").checked = true;
        this.resultsTarget.prepend(child);
      }
    });
    this.resultsTarget.prepend(selectAllCheckbox);
  }

  onResultsMouseDown() {
    this.mouseDown = true;
    this.resultsTarget.addEventListener("mouseup", () => {
      this.mouseDown = false;
    }, { once: true });
  }

  onInputChange() {
    const query = this.inputTarget.value.trim();
    this.fetchResults(query);
  }

  identifyOptions() {
    let id = 0;
    const optionsWithoutId = this.resultsTarget.querySelectorAll(`${optionSelector}:not([id])`);
    optionsWithoutId.forEach((el) => {
      el.id = `${this.resultsTarget.id}-option-${id++}`;
    });
  }

  async fetchResults(query) {

    if (!this.hasUrlValue) return;
    const url = buildURLWithQuery(query, this.urlValue);
    try {
      const html = await fetchHtml(url);

      this.replaceResults(html);
    } catch(error) {
      console.error(error);
    }
  }

  replaceResults(html) {
    this.resultsTarget.innerHTML = DOMPurify.sanitize(html);

    this.checkboxTargets.forEach((checkbox) => {
      const isSelected = this.selectedTargets.some((target) => target.value === checkbox.dataset.formSubmissionValue);
      if (isSelected) {
        const checkboxInput = checkbox.querySelector("input");
        checkboxInput.checked = true;
      }
    });

    if (this.allCheckboxesChecked) this._toggleSelectAllCheckbox();

    this.checkDropdownValues();
    this.identifyOptions();
    if (this.options) {
      this.open();
    } else {
      this.close();
    }
  }

  open() {
    if (this.resultsShown) return;
    this.resultsShown = true;
    this.element.setAttribute("aria-expanded", "true");
  }

  close() {
    if (!this.resultsShown) return;
    this.resultsShown = false;
    this.inputTarget.removeAttribute("aria-activedescendant");
    this.element.setAttribute("aria-expanded", "false");
  }

  get resultsShown() {
    return !this.resultsTarget.hidden;
  }

  set resultsShown(value) {
    this.resultsTarget.hidden = !value;
    this.resultsTarget.classList.toggle("dropdown-search-list-hidden");
  }

  get options() {
    return Array.from(this.resultsTarget.querySelectorAll(optionSelector));
  }

  get selectedOption() {
    return this.resultsTarget.querySelector(activeSelector);
  }

  get selectedClassesOrDefault() {
    return this.hasSelectedClass ? this.selectedClasses : ["active"];
  }

  get selectAllCheckboxInput() {
    return this.selectAllCheckboxTarget.querySelector("input");
  }
}
